import { BulkUploadModal, ValidationError, BulkUploadResult, LanguageColumnMapping } from '@/components/common/BulkUploadModal';
import { Vendor, VendorType, VendorStatus } from '@/types/resources/vendor';
import { useAuth } from '@/contexts/AuthContext';
import { addVendorsBulk } from '@/api/vendors';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { getTemplateURL } from '@/templates/csv/templateLoader';
import { Modal, Progress, Button, Row, Col, Typography, Space, Tag } from 'antd';
import { ShopOutlined, UserOutlined, PhoneOutlined, MailOutlined } from '@ant-design/icons';
import { useState } from 'react';

const { Title, Text } = Typography;

interface VendorsBulkUploadModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

export function VendorsBulkUploadModal({ visible, onCancel, onSuccess }: VendorsBulkUploadModalProps) {
  const { firestoreUser, refreshWorkspaceStats } = useAuth();
  const { t, i18n } = useTranslation(['vendors', 'common']);
  const currentLanguage = i18n.language as 'en' | 'es' | 'pt';
  
  // State for managing the flow
  const [currentStep, setCurrentStep] = useState<'upload' | 'preview' | 'creating'>('upload');
  const [processedData, setProcessedData] = useState<Array<{
    vendor: Omit<Vendor, 'id' | 'workspace' | 'activityRecords'>;
    name: string;
    contactPerson: string;
    email: string;
    phone: string;
    category: string;
    type: string;
    status: string;
  }>>([]);

  // Sample data for template generation
  const sampleData: any[] = [];

  // Column definitions for the template (internationalized)
  const columns = [
    { key: 'name', title: t('vendors:fields.name'), required: true },
    { key: 'contactPerson', title: t('vendors:fields.contactPerson'), required: true },
    { key: 'email', title: t('vendors:fields.email'), required: false },
    { key: 'phone', title: t('vendors:fields.phone'), required: true },
    { key: 'address', title: t('vendors:fields.address'), required: true },
    { key: 'category', title: t('vendors:fields.category'), required: true },
    { key: 'type', title: t('vendors:fields.type'), required: true },
    { key: 'status', title: t('vendors:fields.status'), required: true },
    { key: 'services', title: t('vendors:fields.services'), required: false },
    { key: 'paymentTerms', title: t('vendors:fields.paymentTerms'), required: false },
    { key: 'notes', title: t('vendors:fields.notes'), required: false }
  ];

  // Language-aware column mappings for CSV headers
  const columnMappings: LanguageColumnMapping = {
    en: {
      name: ['name', 'vendor name', 'vendorname', 'company name', 'companyname', 'business name'],
      contactPerson: ['contactperson', 'contact person', 'contact_person', 'contact', 'representative', 'rep'],
      email: ['email', 'email address', 'emailaddress', 'e-mail'],
      phone: ['phone', 'phone number', 'phonenumber', 'telephone', 'tel', 'mobile'],
      address: ['address', 'location', 'street', 'street address'],
      category: ['category', 'cat', 'service category', 'servicecategory', 'business type'],
      type: ['type', 'vendor type', 'vendortype', 'business type', 'businesstype'],
      status: ['status', 'vendor status', 'vendorstatus', 'active status'],
      services: ['services', 'service list', 'servicelist', 'offered services', 'offerings'],
      paymentTerms: ['paymentterms', 'payment terms', 'payment_terms', 'terms', 'payment conditions'],
      notes: ['notes', 'comments', 'description', 'remarks']
    },
    es: {
      name: ['nombre', 'name', 'nombre del proveedor', 'nombredelproveedor', 'nombre de la empresa', 'nombredelaempresa'],
      contactPerson: ['personacontacto', 'persona de contacto', 'persona_contacto', 'contacto', 'representante', 'rep'],
      email: ['email', 'correo electrónico', 'correo', 'e-mail'],
      phone: ['teléfono', 'telefono', 'número de teléfono', 'numerotelefono', 'móvil', 'celular'],
      address: ['dirección', 'direccion', 'ubicación', 'ubicacion', 'calle', 'dirección de la calle'],
      category: ['categoría', 'categoria', 'cat', 'categoría de servicio', 'categoriadeservicio', 'tipo de negocio'],
      type: ['tipo', 'tipo de proveedor', 'tipodeproveedor', 'tipo de negocio', 'tipodenegocio'],
      status: ['estado', 'estado del proveedor', 'estadodelproveedor', 'estado activo'],
      services: ['servicios', 'lista de servicios', 'listadeservicios', 'servicios ofrecidos', 'ofertas'],
      paymentTerms: ['terminospago', 'términos de pago', 'terminos_pago', 'términos', 'condiciones de pago'],
      notes: ['notas', 'comentarios', 'descripción', 'observaciones']
    },
    pt: {
      name: ['nome', 'name', 'nome do fornecedor', 'nomedofornecedor', 'nome da empresa', 'nomedoempresa'],
      contactPerson: ['pessoacontato', 'pessoa de contato', 'pessoa_contato', 'contato', 'representante', 'rep'],
      email: ['email', 'correio eletrônico', 'correio', 'e-mail'],
      phone: ['telefone', 'número de telefone', 'numerotelefone', 'móvel', 'celular'],
      address: ['endereço', 'endereco', 'localização', 'localizacao', 'rua', 'endereço da rua'],
      category: ['categoria', 'cat', 'categoria de serviço', 'categoriadeservico', 'tipo de negócio'],
      type: ['tipo', 'tipo de fornecedor', 'tipodefornecedor', 'tipo de negócio', 'tipodenegocio'],
      status: ['status', 'status do fornecedor', 'statusdofornecedor', 'status ativo'],
      services: ['serviços', 'servicos', 'lista de serviços', 'listadeservicos', 'serviços oferecidos', 'ofertas'],
      paymentTerms: ['termospagamento', 'termos de pagamento', 'termos_pagamento', 'termos', 'condições de pagamento'],
      notes: ['notas', 'comentários', 'descrição', 'observações']
    }
  };

  // Validation function for vendor records
  const validateVendorRecord = (record: any, rowIndex: number): ValidationError[] => {
    const errors: ValidationError[] = [];

    // Required field validation
    if (!record.name || record.name.trim() === '') {
      errors.push({
        row: rowIndex,
        field: 'name',
        value: record.name,
        message: t('vendors:bulkUpload.validation.nameRequired')
      });
    } else if (record.name.length > 100) {
      errors.push({
        row: rowIndex,
        field: 'name',
        value: record.name,
        message: t('vendors:bulkUpload.validation.nameTooLong')
      });
    }

    if (!record.contactPerson || record.contactPerson.trim() === '') {
      errors.push({
        row: rowIndex,
        field: 'contactPerson',
        value: record.contactPerson,
        message: t('vendors:bulkUpload.validation.contactPersonRequired')
      });
    }

    if (!record.phone || record.phone.trim() === '') {
      errors.push({
        row: rowIndex,
        field: 'phone',
        value: record.phone,
        message: t('vendors:bulkUpload.validation.phoneRequired')
      });
    }

    if (!record.address || record.address.trim() === '') {
      errors.push({
        row: rowIndex,
        field: 'address',
        value: record.address,
        message: t('vendors:bulkUpload.validation.addressRequired')
      });
    }

    if (!record.category || record.category.trim() === '') {
      errors.push({
        row: rowIndex,
        field: 'category',
        value: record.category,
        message: t('vendors:bulkUpload.validation.categoryRequired')
      });
    }

    if (!record.type || record.type.trim() === '') {
      errors.push({
        row: rowIndex,
        field: 'type',
        value: record.type,
        message: t('vendors:bulkUpload.validation.typeRequired')
      });
    } else if (!['supplier', 'contractor', 'service_provider'].includes(record.type.toLowerCase())) {
      errors.push({
        row: rowIndex,
        field: 'type',
        value: record.type,
        message: t('vendors:bulkUpload.validation.typeInvalid')
      });
    }

    if (!record.status || record.status.trim() === '') {
      errors.push({
        row: rowIndex,
        field: 'status',
        value: record.status,
        message: t('vendors:bulkUpload.validation.statusRequired')
      });
    } else if (!['active', 'inactive', 'pending'].includes(record.status.toLowerCase())) {
      errors.push({
        row: rowIndex,
        field: 'status',
        value: record.status,
        message: t('vendors:bulkUpload.validation.statusInvalid')
      });
    }

    // Optional field validation
    if (record.email && record.email.trim() !== '') {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(record.email)) {
        errors.push({
          row: rowIndex,
          field: 'email',
          value: record.email,
          message: t('vendors:bulkUpload.validation.emailInvalid')
        });
      }
    }

    if (record.paymentTerms && record.paymentTerms.trim() !== '') {
      if (!['immediate', 'net15', 'net30', 'net60'].includes(record.paymentTerms.toLowerCase())) {
        errors.push({
          row: rowIndex,
          field: 'paymentTerms',
          value: record.paymentTerms,
          message: t('vendors:bulkUpload.validation.paymentTermsInvalid')
        });
      }
    }

    return errors;
  };

  // Transform CSV record to Vendor type
  const transformVendorRecord = (record: any): Omit<Vendor, 'id' | 'workspace' | 'activityRecords'> => {
    return {
      type: record.type.toLowerCase() as VendorType,
      status: record.status.toLowerCase() as VendorStatus,
      name: record.name.trim(),
      contactPerson: record.contactPerson.trim(),
      category: record.category.trim(),
      email: record.email?.trim(),
      phone: record.phone.trim(),
      address: record.address.trim(),
      services: record.services ? record.services.split(',').map((s: string) => s.trim()) : undefined,
      paymentTerms: record.paymentTerms?.toLowerCase() as any,
      notes: record.notes || ''
    };
  };

  // Handle bulk upload confirmation - shows preview first
  const handleBulkUploadConfirm = async (result: BulkUploadResult<Omit<Vendor, 'id' | 'workspace' | 'activityRecords'>>) => {
    if (!firestoreUser) {
      message.error('User not authenticated');
      return;
    }

    try {
      // Process the data to show preview
      const processedData = result.validRecords.map(vendorData => ({
        vendor: vendorData,
        name: vendorData.name,
        contactPerson: vendorData.contactPerson,
        email: vendorData.email || 'N/A',
        phone: vendorData.phone,
        category: vendorData.category,
        type: vendorData.type,
        status: vendorData.status
      }));

      setProcessedData(processedData);
      setCurrentStep('preview');

    } catch (error) {
      console.error('Error processing data:', error);
      message.error('Error processing data. Please try again.');
    }
  };

  // Handle the actual creation after confirmation
  const handleConfirmCreation = async () => {
    if (!firestoreUser || processedData.length === 0) {
      message.error('No data to process');
      return;
    }

    try {
      setCurrentStep('creating');
      
      let totalUploaded = 0;
      const totalRecords = processedData.length;
      
      // Show progress modal
      const progressModal = Modal.info({
        title: 'Creating Vendors',
        content: (
          <div>
            <Progress 
              percent={0} 
              status="active" 
              format={(percent) => `${totalUploaded}/${totalRecords} (${percent}%)`}
            />
            <p>Creating vendors...</p>
          </div>
        ),
        okButtonProps: { style: { display: 'none' } }
      });

      // Prepare vendors data for bulk upload
      const vendorsData = processedData.map(data => data.vendor);

      // Create vendors in bulk
      const result = await addVendorsBulk(vendorsData, firestoreUser, (uploaded, total) => {
        const percent = Math.round((uploaded / total) * 100);
        progressModal.update({
          content: (
            <div>
              <Progress 
                percent={percent} 
                status="active" 
                format={(percent) => `${uploaded}/${total} (${percent}%)`}
              />
              <p>Creating vendors: {uploaded} of {total}</p>
            </div>
          )
        });
      });

      totalUploaded = result.successCount;

      // Refresh workspace stats
      try {
        await refreshWorkspaceStats();
      } catch (error) {
        console.warn('Failed to refresh workspace stats:', error);
      }

      progressModal.destroy();
      message.success(`Successfully created ${totalUploaded} vendors`);
      
      onSuccess();
    } catch (error) {
      console.error('Progress-aware upload error:', error);
      message.error('Error creating vendors. Please try again.');
    }
  };

  // Handle modal cancel
  const handleModalCancel = () => {
    setCurrentStep('upload');
    setProcessedData([]);
    onCancel();
  };

  // Render different content based on current step
  const renderContent = () => {
    switch (currentStep) {
      case 'upload':
        return (
          <BulkUploadModal
            visible={visible}
            onCancel={handleModalCancel}
            onConfirm={handleBulkUploadConfirm}
            title={t('vendors:actions.bulkUpload')}
            templateUrl={getTemplateURL(currentLanguage, 'vendors')}
            templateFileName={`vendors_template_${currentLanguage}.csv`}
            validateRecord={validateVendorRecord}
            transformRecord={transformVendorRecord}
            columns={columns}
            sampleData={sampleData}
            accept=".csv"
            maxFileSize={5}
            language={currentLanguage}
            columnMappings={columnMappings}
          />
        );

      case 'preview':
        return (
          <Modal
            title={t('vendors:bulkUpload.title')}
            open={visible}
            onCancel={handleModalCancel}
            footer={[
              <Button key="cancel" onClick={handleModalCancel}>
                {t('common:actions.cancel')}
              </Button>,
              <Button key="confirm" type="primary" onClick={handleConfirmCreation}>
                {t('common:actions.confirm')}
              </Button>
            ]}
            width={800}
          >
            <div style={{ marginTop: 16 }}>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <Title level={3} style={{ color: '#1890ff', marginBottom: '8px' }}>
                  {t('vendors:bulkUpload.preview.title')}
                </Title>
                <Text style={{ color: '#666', fontSize: '14px' }}>
                  {t('vendors:bulkUpload.preview.subtitle')}
                </Text>
              </div>

              {/* Summary Details */}
              <div style={{ 
                backgroundColor: '#fff', 
                padding: '20px', 
                borderRadius: '6px', 
                border: '1px solid #d9d9d9',
                marginBottom: '24px'
              }}>
                {processedData.map((data, index) => (
                  <div key={index} style={{ 
                    marginBottom: index < processedData.length - 1 ? '20px' : '0',
                    padding: '16px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px',
                    border: '1px solid #e9ecef'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                      <div style={{ 
                        width: '40px', 
                        height: '40px', 
                        backgroundColor: '#1890ff', 
                        borderRadius: '50%', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        marginRight: '16px'
                      }}>
                        <ShopOutlined style={{ color: '#fff', fontSize: '18px' }} />
                      </div>
                      <div>
                        <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
                          {data.name}
                        </Title>
                        <Text style={{ margin: 0, color: '#666', fontSize: '12px' }}>
                          {data.contactPerson} • {data.category}
                        </Text>
                      </div>
                    </div>

                    <Row gutter={16}>
                      <Col span={6}>
                        <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#f6ffed', borderRadius: '6px', border: '1px solid #b7eb8f' }}>
                          <Tag color="green" style={{ margin: 0 }}>
                            {data.status}
                          </Tag>
                          <div style={{ fontSize: '12px', color: '#52c41a', marginTop: '4px' }}>
                            Status
                          </div>
                        </div>
                      </Col>
                      <Col span={6}>
                        <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#e6f7ff', borderRadius: '6px', border: '1px solid #91d5ff' }}>
                          <Tag color="blue" style={{ margin: 0 }}>
                            {data.type}
                          </Tag>
                          <div style={{ fontSize: '12px', color: '#1890ff', marginTop: '4px' }}>
                            Type
                          </div>
                        </div>
                      </Col>
                      <Col span={6}>
                        <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#fff7e6', borderRadius: '6px', border: '1px solid #ffd591' }}>
                          <PhoneOutlined style={{ fontSize: '16px', color: '#fa8c16' }} />
                          <div style={{ fontSize: '12px', color: '#fa8c16', marginTop: '4px' }}>
                            {data.phone}
                          </div>
                        </div>
                      </Col>
                      <Col span={6}>
                        <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#f0f0f0', borderRadius: '6px', border: '1px solid #d9d9d9' }}>
                          <MailOutlined style={{ fontSize: '16px', color: '#666' }} />
                          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                            {data.email}
                          </div>
                        </div>
                      </Col>
                    </Row>

                    <div style={{ marginTop: '12px', padding: '8px', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
                      <Space>
                        <UserOutlined />
                        <Text style={{ fontSize: '12px' }}>
                          {data.vendor.address}
                        </Text>
                      </Space>
                    </div>
                  </div>
                ))}

                {/* Confirmation Message */}
                <div style={{ 
                  marginTop: '20px', 
                  padding: '16px', 
                  backgroundColor: '#fff2e8', 
                  borderRadius: '6px', 
                  border: '1px solid #ffbb96',
                  textAlign: 'center'
                }}>
                  <Text style={{ margin: 0, color: '#d46b08', fontSize: '14px' }}>
                    {t('vendors:bulkUpload.preview.confirmationMessage', {
                      count: processedData.length
                    })}
                  </Text>
                </div>
              </div>
            </div>
          </Modal>
        );

      case 'creating':
        return (
          <Modal
            title="Creating Vendors..."
            open={visible}
            onCancel={handleModalCancel}
            footer={null}
            width={600}
          >
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Progress type="circle" percent={100} status="active" />
              <Text style={{ display: 'block', marginTop: '16px' }}>
                Please wait while the vendors are being created...
              </Text>
            </div>
          </Modal>
        );

      default:
        return null;
    }
  };

  return renderContent();
}
